import { createContext, FC, PropsWithChildren, useContext, useState } from "react";
import MileageRemindersNotNotifiedDialog from "../components/mileages/MileageRemindersNotNotifiedDialog";
import { useI18n } from "../hooks/useTranslations";
import { useAdminMileagesApi } from "../http/mileages/api";
import { MileageReminderNotNotified, SendMileageRemindersBody } from "../http/mileages/types";
import NotificationDispatch, { showErrorNotification, showSuccessNotification } from "./notificationContext";

type MileageRemindersContextType = {
  // while a sending is in progress a new one can't be started, to avoid duplicated emails
  isSending: boolean;
  send: (body: SendMileageRemindersBody) => Promise<void>;
};

const MileageRemindersContext = createContext<MileageRemindersContextType | null>(null);

// the mileage reminders are sent in background: the result is shown even if the sending dialog is closed or the page is changed
export const MileageRemindersProvider: FC<PropsWithChildren> = ({ children }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const mileagesApi = useAdminMileagesApi();
  const [isSending, setIsSending] = useState(false);
  const [notNotified, setNotNotified] = useState<MileageReminderNotNotified[]>([]);

  const send = async (body: SendMileageRemindersBody) => {
    setIsSending(true);
    try {
      const result = await mileagesApi.sendMileageReminders(body);

      if (result.notNotified.length > 0) {
        setNotNotified(result.notNotified);
      } else {
        dispatch(showSuccessNotification(i18n.t, i18n.t.mileages.remindersDialog.sent));
      }
    } catch (error) {
      // sticky: in the meantime the user may be working on something else
      dispatch(showErrorNotification(i18n, error, undefined, true));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <MileageRemindersContext.Provider value={{ isSending, send }}>
      {children}

      {notNotified.length > 0 && (
        <MileageRemindersNotNotifiedDialog notNotified={notNotified} onClose={() => setNotNotified([])} />
      )}
    </MileageRemindersContext.Provider>
  );
};

export const useMileageReminders = () => {
  const context = useContext(MileageRemindersContext);
  if (!context) throw new Error("useMileageReminders must be used within MileageRemindersProvider");

  return context;
};
